import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  ButtonDropdown,
  ButtonDropdownProps,
  Checkbox,
  ContentLayout,
  Header,
  Link,
  Modal,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';

import { useGetUserInformation } from 'appSelectorContext';

import useLocalStorage from '../../hooks/useLocalStorage';
import { Context } from '../../store/context';
import { ReceiveType, Site, URIParams } from '../../store/types';
import ExceptionModal, { DEFAULT_EXCEPTION_STATE } from '../ExceptionsModal';
import SiteInput from '../SiteInput';
import { getStage } from '../Utils';
import { getSiteDeprecationStatus } from '../../constants/siteDeprecations';
import { Notifications } from './Notifications';

import './layout.scss';

interface LayoutProps {
  children: React.ReactNode;
  description: string | undefined;
  title: string | undefined;
  onSiteChange: (site: Site) => void;
  hideLayout?: boolean;
}

export const Layout = ({ children, description, title, onSiteChange, hideLayout = false }: LayoutProps) => {
  const { state } = useContext(Context);
  const [settings, showSettings] = useState<boolean>(false);
  const [visible, setVisible] = useState(false);
  const [scanModels, setScanModelsDialog] = useLocalStorage('scan-models-dialog', true);
  const [selectedSite, setSelectedSite] = useLocalStorage('unifiedReceivePreferredSite', null);
  const [release1, setRelease1] = useLocalStorage('release-1-experience', false);
  const [showPrefs] = useLocalStorage('show-receive-user-preferences', false);
  const { user, signOut } = useGetUserInformation();
  const urlParam = useParams<URIParams>();
  const isAdjustment = urlParam.receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  // TODO: [9 Feb 2024] Remove these changes in the next deployment
  // maintenance variables.
  const [showMaintenanceAlert, setShowMaintenanceAlert] = useState<boolean>(false);
  const maintenanceStart = new Date('March 07, 2024 9:00:00 PST');
  const maintenanceEnd = new Date('March 07, 2024 13:00:00 PST');
  const currentTime = new Date();

  const onHandleItemClick = ({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) => {
    if (detail?.id === 'appSettings') {
      showSettings(true);
    }

    if (detail?.id === 'signout' && typeof signOut === 'function') {
      signOut();
    }
  };

  const onHandleSiteChange = (site: Site) => {
    setSelectedSite(site);
    onSiteChange && onSiteChange(site);
  };

  useEffect(() => {
    function setMode() {
      if (window?.matchMedia('(prefers-color-scheme: dark)')?.matches) {
        document?.body?.classList?.remove('awsui-polaris-light-mode');
        document?.body?.classList?.add('awsui-polaris-dark-mode');
      } else {
        document?.body?.classList?.remove('awsui-polaris-dark-mode');
        document?.body?.classList?.add('awsui-polaris-light-mode');
      }
    }

    window?.matchMedia('(prefers-color-scheme: dark)')?.addEventListener('change', () => {
      setMode();
    });
    setMode();
  }, []);

  // TODO: [9 Feb 2024]Remove these changes in the next deployment
  const shouldShowMaintenanceAlert = () => {
    const stage: string = getStage();
    return (
      (stage === 'prodzhy' || stage === 'betazhy') && currentTime >= maintenanceStart && currentTime <= maintenanceEnd
    );
  };

  useEffect(() => {
    if (shouldShowMaintenanceAlert()) {
      setShowMaintenanceAlert(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHandleShowExceptions = () => {
    setVisible(true);
  };

  const displaySitesDropdown =
    (isAdjustment && state?.hasPermissionsToAccessIA) || (!isAdjustment && state?.hasPermissionsToAccessReceive);

  // Site deprecation banner
  const siteValue = selectedSite?.value || '';
  const deprecation = getSiteDeprecationStatus(siteValue);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Reset dismissed state when site changes
  useEffect(() => {
    setBannerDismissed(false);
  }, [siteValue]);

  const showDeprecationBanner =
    (deprecation.status === 'warning' || deprecation.status === 'blocked') &&
    (!bannerDismissed || !deprecation.dismissible);

  const renderContent = () => {
    return (
      <>
        {/* TODO: [9 Feb 2024]Remove these changes in the next deployment  */}
        {showMaintenanceAlert && (
          <Alert statusIconAriaLabel="Info" type="warning">
            {`Receive ZHY will be down on PLACEHOLDER_TEXT due to scheduled Mobility ZHY database
            maintenance, affecting serial validations and container validations and recommendations.`}
          </Alert>
        )}

        {showDeprecationBanner && (
          <Alert
            statusIconAriaLabel={deprecation.status === 'blocked' ? 'Error' : 'Warning'}
            type={deprecation.status === 'blocked' ? 'error' : 'warning'}
            dismissible={deprecation.dismissible}
            onDismiss={() => setBannerDismissed(true)}
          >
            {deprecation.message}
            {deprecation.status === 'blocked' && deprecation.boostLink && (
              <>
                {' '}
                <Link href={deprecation.boostLink} external>
                  Go to Boost Receive.
                </Link>
              </>
            )}
          </Alert>
        )}

        {children}
        {visible && (
          <ExceptionModal
            formState={{ type: '', values: { ...DEFAULT_EXCEPTION_STATE, SITE: selectedSite?.value } }}
            show
            onDismiss={() => setVisible(false)}
          />
        )}
        <Modal onDismiss={() => showSettings(false)} visible={settings} header="User Preferences">
          <h3>Select User Preferences</h3>
          <Checkbox onChange={({ detail }) => setScanModelsDialog(detail.checked)} checked={scanModels}>
            Show Scan Parts as Modal Dialog
          </Checkbox>
          <Checkbox onChange={({ detail }) => setRelease1(detail.checked)} checked={release1}>
            Show release 1 experience
          </Checkbox>
        </Modal>
      </>
    );
  };

  if (state?.hideLayout) {
    return renderContent();
  }

  return (
    <ContentLayout
      headerBackgroundStyle="#000716"
      headerVariant="high-contrast"
      header={
        <Box margin={{ top: 'm' }}>
          <SpaceBetween size="m">
            <Header
              variant="h1"
              description={description}
              actions={
                <div className="receive-header">
                  <SpaceBetween size="s" direction="horizontal">
                    {displaySitesDropdown && <Notifications onClickExceptionLink={onHandleShowExceptions} />}
                    {displaySitesDropdown && (
                      <SiteInput
                        isPreferredSite={true}
                        onSiteChange={onHandleSiteChange}
                        siteName={selectedSite?.value}
                      />
                    )}
                    <ButtonDropdown
                      variant="normal"
                      onItemClick={onHandleItemClick}
                      items={[
                        {
                          text: 'Account',
                          items: [
                            { text: 'My Permissions', id: 'permissions', disabled: true },
                            { text: 'My Subscriptions', id: 'subscriptions', disabled: true },
                            { text: 'Sign Out', id: 'signout' },
                          ],
                        },
                        {
                          text: 'Preferences',
                          items: [
                            { text: 'Language', id: 'language', disabled: true },
                            { text: 'Timezone', id: 'timezone', disabled: true },
                            { text: 'User Preferences', id: 'appSettings', disabled: !showPrefs },
                          ],
                        },
                      ]}
                    >
                      {`Hello ${user}`}
                    </ButtonDropdown>
                  </SpaceBetween>
                </div>
              }
            >
              {title}
            </Header>
          </SpaceBetween>
        </Box>
      }
    >
      {renderContent()}
    </ContentLayout>
  );
};
